import Collapse from 'antd/lib/collapse'
import styles from './JSONAnnotationTask.module.less'
import { TaskMode } from 'Types/Tasks.types'
import CheckCircleFilledIcon from 'src/assets/icons/check-circle-filled.svg?react'
import IncompleteIcon from 'src/assets/icons/incomplete.svg?react'
import Flex from 'antd/lib/flex'
import { JSONObject, JSONSchemaMetaData } from 'Types/Common.types'
import { useMemo } from 'react'
import { getDisplayName, getMetadataForPath, getOrder } from '../utils'

type DynamicCollapseViewProps = {
    mode: TaskMode
    data: JSONObject
    title: string
    path: string
    level: number
    isReadOnly: () => boolean
    getStatusForPath: (path: string) => { isComplete: boolean } | undefined
    onSelectPath: (path: string) => void
    selectedPath: string | null
    metadata: JSONSchemaMetaData | null
}

export const DynamicCollapseView = ({
    data,
    title,
    path,
    mode,
    level,
    isReadOnly,
    getStatusForPath,
    onSelectPath,
    selectedPath,
    metadata,
}: DynamicCollapseViewProps) => {
    const isArray = Array.isArray(data)
    const currentPathMetadata = useMemo(() => getMetadataForPath(metadata, path), [metadata, path])

    const entriesWithMetadata = useMemo(() => {
        return Object.entries(data)
            .map(([key, value], index) => {
                const childPath = isArray ? `${path}[${key}]` : `${path}.${key}`
                return {
                    key,
                    value,
                    index,
                    childPath,
                    displayName: getDisplayName(key, currentPathMetadata, isArray, index),
                    order: getOrder(key, currentPathMetadata, isArray, index),
                }
            })
            .sort((a, b) => a.order - b.order)
    }, [data, path, isArray, currentPathMetadata])

    const renderPrimitive = (childPath: string, displayName: string) => {
        const status = getStatusForPath(childPath)
        const isContainerSelected = level === 0 && selectedPath?.startsWith(childPath)

        let selectionClass = ''

        if (selectedPath === childPath) {
            selectionClass = styles['selected']
        } else if (level !== 0) {
            selectionClass = styles['not-selected']
        }

        const classNames = [
            level === 0 ? styles['primitive-property-1'] : styles['primitive-property-all'],
            selectionClass,
            isContainerSelected ? styles['selected-container'] : '',
        ]
            .filter(Boolean)
            .join(' ')

        return (
            <Flex
                justify="space-between"
                align="center"
                onClick={e => {
                    e.stopPropagation()
                    onSelectPath(childPath)
                }}
                key={childPath}
                style={level === 0 ? {} : { margin: '8px 12px' }}
                className={classNames}
            >
                <div className={styles['title']}>{displayName}</div>
                {status?.isComplete === false && <IncompleteIcon data-testid="dashed-circle" />}
                {status?.isComplete && (
                    <CheckCircleFilledIcon data-testid="check-circle-filled-icon" />
                )}
            </Flex>
        )
    }

    const children = entriesWithMetadata.map(({ value, childPath, displayName }) => {
        if (value && typeof value === 'object') {
            return (
                <DynamicCollapseView
                    key={childPath}
                    data={value as JSONObject}
                    title={displayName}
                    path={childPath}
                    mode={mode}
                    level={level + 1}
                    isReadOnly={isReadOnly}
                    getStatusForPath={getStatusForPath}
                    onSelectPath={onSelectPath}
                    selectedPath={selectedPath}
                    metadata={metadata}
                />
            )
        } else {
            return renderPrimitive(childPath, displayName)
        }
    })

    if (path === '$') return <div key={path}>{children}</div>

    const status = getStatusForPath(path)
    const isContainerSelected = level === 1 && selectedPath?.startsWith(path)

    const renderCollapseHeader = () => (
        <Flex
            justify="space-between"
            align="center"
            onClick={e => {
                e.stopPropagation()
                onSelectPath(path)
            }}
            className={selectedPath === path ? styles['selected'] : ''}
        >
            <div className={styles['title']}>
                {getDisplayName(title, currentPathMetadata, false, 0)}
            </div>
            {status?.isComplete === false && <IncompleteIcon data-testid="dashed-circle" />}
            {status?.isComplete && <CheckCircleFilledIcon data-testid="check-circle-filled-icon" />}
        </Flex>
    )

    return (
        <Collapse
            className={styles['collapse-panel']}
            style={{ marginBottom: level === 1 ? '16px' : '0px' }}
            data-testid={`dynamic-collapse-${path}`}
            expandIconPosition="start"
            bordered={level === 1}
            ghost={level !== 1}
            size={level === 1 ? 'middle' : 'small'}
            items={[
                {
                    key: path,
                    label: renderCollapseHeader(),
                    children,
                    className: [
                        level === 0 ? '' : styles['left-border'],
                        level === 1 ? styles['collapse-header-1'] : styles['collapse-header-not-1'],
                        isContainerSelected ? styles['selected-container'] : '',
                    ]
                        .filter(Boolean)
                        .join(' '),
                    forceRender: true,
                },
            ]}
        />
    )
}
